import { CircularProgress } from '@material-ui/core';
import { AutocompleteInputChangeReason } from '@mui/material';
import { ChangeEvent, ComponentProps, useRef } from 'react';

import Autocomplete from 'src/components/Autocomplete';
import { useDebounceInput } from 'src/hooks/useDebounceInput';
import { BaseTextField } from 'src/legacy/components/TextField';
import {
  GoogleMapPrediction,
  useGetGoogleMapsAutocompleteQuery,
} from 'src/services/api/googleMapsApi';

interface GoogleMapsAddressInputProps
  extends Pick<
    ComponentProps<typeof BaseTextField>,
    'label' | 'error' | 'helperText'
  > {
  addressValue: string;
  onAddressValueChange: (e: ChangeEvent<HTMLInputElement>) => void;
  country: string;
  autoFocus?: boolean;
  onAutocompleteChange: (value: GoogleMapPrediction) => void;
  onAutocompleteInputChange: (
    newValue: string,
    reason: AutocompleteInputChangeReason,
  ) => void;
}

const GoogleMapsAddressInput = ({
  addressValue,
  onAddressValueChange,
  country,
  label,
  error,
  helperText,
  autoFocus,
  onAutocompleteChange,
  onAutocompleteInputChange,
}: GoogleMapsAddressInputProps) => {
  const hasAddressChanged = useRef(false);
  const [debouncedAddressValue, onDebouncedAddressChange] = useDebounceInput(
    addressValue,
    (e) => {
      if (e.target.value !== addressValue) {
        hasAddressChanged.current = true;
        onAddressValueChange(e);
      }
    },
  );

  const { data, isFetching } = useGetGoogleMapsAutocompleteQuery(
    { search: addressValue, country },
    { skip: addressValue === '' || !hasAddressChanged.current },
  );

  return (
    <Autocomplete
      classes={{
        option: 'min-h-0',
        paper: 'relative right-[1px]',
      }}
      sx={{
        // Counter MUI v5 Autocomplete defaults that inflate the input past
        // BaseTextField's 30px skinny height (see MuiAutocomplete.ts comment).
        '& .MuiOutlinedInput-root': { padding: '0px 7px' },
        '&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root':
          { paddingRight: '14px' },
        '& .MuiAutocomplete-input': { paddingTop: 0, paddingBottom: 0 },
      }}
      freeSolo
      options={data?.predictions ?? []}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.addressLine1
          ? `${option.addressLine1} ${option.addressLine2}`
          : option.addressLine2;
      }}
      renderOption={(props, option) => <li {...props}>{option.fullAddress}</li>}
      filterOptions={(x) => x}
      onChange={(_, value) => {
        if (!value || typeof value === 'string') {
          return;
        }
        onAutocompleteChange(value);
      }}
      inputValue={addressValue}
      onInputChange={(_, newValue, reason) => {
        onAutocompleteInputChange(newValue, reason);
      }}
      renderInput={(params) => (
        <BaseTextField
          {...params}
          value={debouncedAddressValue}
          onChange={onDebouncedAddressChange}
          label={label}
          variant="outlined"
          sizeVariant="skinny"
          fullWidth
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            className: '',
            autoFocus,
            endAdornment: isFetching ? (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: 20,
                  height: 20,
                }}
              >
                <CircularProgress size={20} />
              </span>
            ) : null,
            inputProps: { ...params.inputProps, 'data-1p-ignore': true },
          }}
        />
      )}
    />
  );
};

export default GoogleMapsAddressInput;
